import { Checkbox } from '@odekoteam/doppio';
import SvgIconSortArrowSwap from 'assets/icons/SvgIconSortArrowSwap';
import SvgIconSortNumberAscending from 'assets/icons/SvgIconSortNumberAscending';
import SvgIconSortNumberDescending from 'assets/icons/SvgIconSortNumberDescending';
import type { MouseEventHandler, ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import { ThemeUICSSObject } from 'theme-ui';
import { cellStyle, rowStyle, tableStyle } from './style';

export const Table = ({
  children,
  style,
  ...rest
}: {
  children: ReactNode;
  id?: string;
  style?: ThemeUICSSObject;
}): JSX.Element => (
  <table sx={{ ...tableStyle, ...style }} {...rest}>
    {children}
  </table>
);

export const TableHeaderCells = ({
  labels,
  onHeaderSelected,
  isAllSelected,
  setIsAllSelected,
  style,
  fontVariant = 'text.paragraphLgEmphasis',
  ...rest
}: {
  labels: (ReactNode | string)[];
  isAllSelected?: boolean;
  setIsAllSelected?: React.Dispatch<React.SetStateAction<boolean>>;
  onHeaderSelected?: (value: boolean) => void;
  style?: ThemeUICSSObject;
  fontVariant?: string;
}): JSX.Element => {
  const headerStyle: ThemeUICSSObject = {
    variant: fontVariant,
    backgroundColor: 'backgroundWeak',
    overflow: 'hidden',
    color: 'secondaryText',
  };
  return (
    <>
      {labels.map((label, index) => {
        const getBorderRadius = (): string => {
          if (index === 0) return '4px 0px 0px 4px';
          if (index === labels.length - 1) return '0px 4px 4px 0px';
          return '0px';
        };
        if (label === '<select>')
          return (
            <th
              key={index}
              sx={{
                ...headerStyle,
                ...cellStyle,
                borderRadius: getBorderRadius(),
                ...style,
              }}
              data-test-id="table-select"
            >
              <Checkbox
                isChecked={isAllSelected}
                onChange={(value): void => {
                  if (onHeaderSelected) onHeaderSelected(value);
                  if (setIsAllSelected) setIsAllSelected(value);
                }}
              />
            </th>
          );
        return (
          <th
            key={index}
            sx={{
              ...headerStyle,
              ...cellStyle,
              borderRadius: getBorderRadius(),
              ...style,
            }}
          >
            {label}
          </th>
        );
      })}
    </>
  );
};

export const TableHeader = ({
  labels,
  onHeaderSelected,
  isAllSelected,
  setIsAllSelected,
  style,
  fontVariant = 'text.paragraphLgEmphasis',
  ...rest
}: {
  labels: (ReactNode | string)[];
  isAllSelected?: boolean;
  setIsAllSelected?: React.Dispatch<React.SetStateAction<boolean>>;
  onHeaderSelected?: (value: boolean) => void;
  style?: ThemeUICSSObject;
  fontVariant?: string;
}): JSX.Element => {
  return (
    <thead>
      <tr>
        <TableHeaderCells
          labels={labels}
          onHeaderSelected={onHeaderSelected}
          isAllSelected={isAllSelected}
          setIsAllSelected={setIsAllSelected}
          style={style}
        />
      </tr>
    </thead>
  );
};

export const SortableTableHeader = ({
  columns,
  fontVariant = 'text.paragraphLgEmphasis',
  sortOptions,
  onSort,
  ...rest
}: {
  columns: { label: string; sortable: boolean }[];
  sortOptions:
    | {
        sortBy: string;
        direction: 'asc' | 'desc';
      }
    | undefined
    | null;
  fontVariant?: string;
  onSort: (sortBy: string) => void;
}): JSX.Element => {
  const getSortIcon = (label: string): JSX.Element => {
    if (!!sortOptions && sortOptions.sortBy === label) {
      return sortOptions.direction === 'asc' ? (
        <SvgIconSortNumberAscending />
      ) : (
        <SvgIconSortNumberDescending />
      );
    }
    return <SvgIconSortArrowSwap />;
  };

  const getAriaSort = (
    label: string,
    sortable: boolean,
  ): 'ascending' | 'descending' | 'none' | undefined => {
    if (!sortable) return undefined;
    if (!sortOptions || sortOptions.sortBy !== label) return 'none';
    return sortOptions.direction === 'asc' ? 'ascending' : 'descending';
  };

  return (
    <thead>
      <tr
        sx={{
          variant: fontVariant,
          backgroundColor: 'backgroundWeak',
          overflow: 'hidden',
          color: 'secondaryText',
        }}
      >
        {columns.map(({ label, sortable }) => {
          const isActive =
            sortable && sortOptions && sortOptions.sortBy === label;
          const ariaSort = getAriaSort(label, sortable);
          const content = (
            <>
              <span>{label}</span>
              {sortable && (
                <span
                  className="sort-icon"
                  sx={{ visibility: isActive ? 'visible' : 'hidden' }}
                >
                  {getSortIcon(label)}
                </span>
              )}
            </>
          );

          return (
            <th
              key={label}
              aria-sort={ariaSort}
              sx={{
                ...cellStyle,
                cursor: 'default',
              }}
            >
              {sortable ? (
                <button
                  type="button"
                  sx={{
                    display: 'flex',
                    gap: 2,
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    color: 'inherit',
                    font: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                    '&:hover .sort-icon': { visibility: 'visible' },
                    '&:focus-visible': {
                      outline: '2px solid currentColor',
                      outlineOffset: '2px',
                    },
                  }}
                  onClick={(): void => onSort(label)}
                >
                  {content}
                </button>
              ) : (
                <div
                  sx={{
                    display: 'flex',
                    gap: 2,
                  }}
                >
                  {content}
                </div>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export const TableBody = ({
  children,
  ...rest
}: {
  children: ReactNode;
}): JSX.Element => <tbody {...rest}>{children}</tbody>;

export const TableRow = ({
  children,
  style,
  onClick,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: {
  children: ReactNode;
  style?: ThemeUICSSObject;
  onClick?: MouseEventHandler<HTMLTableRowElement>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}): JSX.Element => (
  <tr
    {...rest}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    sx={{ ...rowStyle, ...style }}
  >
    {children}
  </tr>
);

export const TableCell = ({
  style,
  children,
  onClick,
  colSpan,
  ...rest
}: {
  style?: ThemeUICSSObject;
  onClick?: MouseEventHandler<HTMLTableCellElement>;
  colSpan?: number;
  children: ReactNode;
}): JSX.Element => (
  <td
    {...rest}
    colSpan={colSpan}
    sx={{ ...cellStyle, ...style }}
    onClick={onClick}
  >
    {children}
  </td>
);

export const LoaderTable = ({
  numColumns,
  numRows = 3,
}: {
  numColumns: number;
  numRows?: number;
}): JSX.Element => {
  return (
    <>
      {Array.from({ length: numRows }, (_, i) => (
        <TableRow key={`loader-row-${i}`}>
          {Array.from({ length: numColumns }, (_, j) => (
            <TableCell key={`loader-cell-${j}`}>
              <Skeleton sx={{ height: '24px' }} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

export const LoadingRow = ({ numCells }: { numCells: number }): JSX.Element => {
  return (
    <TableRow>
      {Array(numCells).fill(
        <TableCell>
          <Skeleton sx={{ zIndex: 0 }} />
        </TableCell>,
      )}
    </TableRow>
  );
};
